import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { StateView } from '@/card/record/StateView'
import { Icon } from '@/components/Icon'
import type { Locale } from '@/i18n'
import { ApiError } from '@/lib/api'
import { formatDecimal, formatMoney, formatNumber } from '@/lib/format'
import { fetchPage } from '@/lists/api'
import type { ListRow } from '@/lists/types'
import { iso } from './types'
import type { Period } from './types'
import '@/styles/work-analytics.css'

/**
 * Общие куски экранов отчётов на дизайн-системе (SalesReports, FinanceReports,
 * StockReports, ReportBuilder, ReportMailings).
 *
 * Отдельно от `ReportShell`: тот держит обвязку дашбордов (#77), и перекраска его разметки
 * меняла бы чужой экран. Здесь — только то, что нужно новым макетам: деньги прочерком
 * вместо нуля, состояние словами, выбор периода шагом.
 */

// ------------------------------------------------------------------ числа

/** Число из ответа сервера: BigDecimal приезжает строкой. `null` — значимое «нет числа». */
export function num(value: string | number | null | undefined): number | null {
  if (value == null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Сумма с валютой или прочерк.
 *
 * Прочерк, а не ноль: у отчётов `null` значим (маржа без себестоимости, CAC без затрат), и
 * ноль на его месте — утверждение, которого никто не делал.
 */
export function moneyText(
  value: string | number | null | undefined,
  currency: string | null | undefined,
  locale: Locale,
): string {
  if (num(value) == null) return '—'
  // The server's string goes to the formatter as is: parsing it first would round kopecks.
  return formatMoney(value, currency ?? null, locale)
}

export function Money({
  value,
  currency,
}: {
  value: string | number | null | undefined
  currency?: string | null
}) {
  const { locale, t } = useApp()
  const text = moneyText(value, currency ?? null, locale)
  if (text === '—') {
    return (
      <span className="muted" title={t('rk.noValue')}>
        —
      </span>
    )
  }
  return <>{text}</>
}

/** Доля в процентах с одним знаком; `null` — прочерк. */
export function percentText(value: number | null, locale: Locale): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${formatDecimal(value, 1, locale)} %`
}

/** Число или прочерк — для количеств и коэффициентов. */
export function NumberValue({ value, digits }: { value: string | number | null | undefined; digits?: number }) {
  const { locale, t } = useApp()
  const parsed = num(value)
  if (parsed == null) {
    return (
      <span className="muted" title={t('rk.noValue')}>
        —
      </span>
    )
  }
  return (
    <>
      {digits === undefined
        ? formatNumber(parsed, locale)
        : formatDecimal(parsed, digits, locale)}
    </>
  )
}

/**
 * Сумма одной валюты по строкам или `null`, если валют несколько.
 *
 * Сложить рубли с евро значит выдумать курс; итог по смешанным валютам не показывается
 * вовсе, а не показывается неверным.
 */
export function singleCurrencyTotal<T>(
  rows: T[],
  amount: (row: T) => string | number | null | undefined,
  currency: (row: T) => string | null | undefined,
): { amount: number; currency: string | null } | null {
  const currencies = new Set(rows.map(currency).filter((code): code is string => Boolean(code)))
  if (currencies.size > 1) return null
  let total = 0
  for (const row of rows) total += num(amount(row)) ?? 0
  return { amount: total, currency: currencies.values().next().value ?? null }
}

// ------------------------------------------------------------------ длительности

/**
 * Длительность в днях.
 *
 * `java.time.Duration` приходит ISO-строкой (`PT312H30M`) или числом секунд — в зависимости
 * от настроек сериализации; разбираются оба вида, иначе цикл сделки молча станет прочерком.
 */
export function durationDays(value: string | number | null | undefined): number | null {
  if (value == null || value === undefined || value === '') return null
  if (typeof value === 'number') return Number.isFinite(value) ? value / 86400 : null
  const numeric = Number(value)
  if (Number.isFinite(numeric)) return numeric / 86400
  const match = /^(-)?P(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(value)
  if (!match) return null
  const [, minus, d, h, m, s] = match
  const seconds =
    Number(d ?? 0) * 86400 + Number(h ?? 0) * 3600 + Number(m ?? 0) * 60 + Number(s ?? 0)
  return (minus ? -seconds : seconds) / 86400
}

/** «19 дн.» или прочерк. */
export function daysText(days: number | null, t: (key: string, p?: Record<string, string | number>) => string, locale: Locale): string {
  if (days == null) return '—'
  const rounded = days < 10 ? Math.round(days * 10) / 10 : Math.round(days)
  return t('rk.days', { days: formatNumber(rounded, locale) })
}

// ------------------------------------------------------------------ люди

/** Инициалы для аватара: «Менеджеров М.» → «ММ». */
export function initials(name: string | null | undefined): string {
  if (!name) return '·'
  const parts = name.replace(/[^\p{L}\s.-]/gu, '').split(/[\s.]+/).filter(Boolean)
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
}

// ------------------------------------------------------------------ состояния

/** 403 и отказ по источнику — запрет, остальное — ошибка. */
export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && (error.isForbidden || error.code === 'report.forbidden_source')
}

/** Технический след для поддержки: код ответа и машинный код. */
export function errorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined
  return `${error.status}${error.code ? ` · ${error.code}` : ''}`
}

/**
 * Ошибка запроса словами: запрет — запретом, сбой — сбоем с «Повторить».
 *
 * «Повторить» на отказ по правам отправляет человека жать кнопку, которая не поможет, а
 * пустая таблица на месте отказа читается как «данных нет».
 */
export function QueryState({
  error,
  errorTitle,
  forbiddenTitle,
  forbiddenText,
  onRetry,
  boxed,
  compact,
}: {
  error: unknown
  errorTitle: string
  forbiddenTitle?: string
  forbiddenText?: ReactNode
  onRetry?: () => void
  boxed?: boolean
  compact?: boolean
}) {
  const { t } = useApp()
  if (isForbidden(error)) {
    return (
      <StateView
        kind="forbidden"
        boxed={boxed}
        compact={compact}
        title={forbiddenTitle ?? t('state.forbidden.title')}
        description={
          forbiddenText ?? (error instanceof ApiError && error.message ? error.message : t('state.forbidden.text'))
        }
      />
    )
  }
  return (
    <StateView
      kind="error"
      boxed={boxed}
      compact={compact}
      title={errorTitle}
      description={error instanceof ApiError && error.message ? error.message : t('rk.error.text')}
      actions={
        onRetry ? (
          <button className="btn btn-secondary btn-sm" type="button" onClick={onRetry}>
            <Icon name="refresh" size="sm" />
            {t('state.retry')}
          </button>
        ) : undefined
      }
      code={errorCode(error)}
    />
  )
}

/** Скелет строк таблицы или панели, пока идёт запрос. */
export function SkeletonLines({ rows = 5, label }: { rows?: number; label?: string }) {
  return (
    <div className="panel-b stack" style={{ gap: 12 }} aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }, (_, index) => (
        <span key={index} className="skel" style={{ width: `${55 + ((index * 17) % 40)}%` }} />
      ))}
    </div>
  )
}

// ------------------------------------------------------------------ период

export type PeriodStep = 'month' | 'quarter' | 'year'

export interface StepPeriod extends Period {
  step: PeriodStep
  /** Любая дата внутри периода — опора для «предыдущий/следующий». */
  anchor: string
}

/** Календарный период шага, содержащий дату. */
export function periodOf(step: PeriodStep, anchor: Date): StepPeriod {
  const year = anchor.getFullYear()
  const month = anchor.getMonth()
  let start: Date
  let end: Date
  if (step === 'month') {
    start = new Date(year, month, 1)
    end = new Date(year, month + 1, 0)
  } else if (step === 'quarter') {
    const first = Math.floor(month / 3) * 3
    start = new Date(year, first, 1)
    end = new Date(year, first + 3, 0)
  } else {
    start = new Date(year, 0, 1)
    end = new Date(year, 11, 31)
  }
  return { step, from: iso(start), to: iso(end), anchor: iso(start) }
}

/** Соседний период того же шага. */
export function shiftPeriod(period: StepPeriod, direction: -1 | 1): StepPeriod {
  const [year, month] = period.anchor.split('-').map(Number) as [number, number]
  const months = period.step === 'month' ? 1 : period.step === 'quarter' ? 3 : 12
  return periodOf(period.step, new Date(year, month - 1 + direction * months, 1))
}

/** Подпись периода: «3 кв. 2026», «октябрь 2026», «2026». */
export function periodLabel(
  period: StepPeriod,
  t: (key: string, p?: Record<string, string | number>) => string,
  locale: Locale,
): string {
  const [year, month] = period.anchor.split('-').map(Number) as [number, number]
  if (period.step === 'year') return String(year)
  if (period.step === 'quarter') return t('rk.quarter', { q: Math.floor((month - 1) / 3) + 1, year })
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(year, month - 1, 1))
}

/**
 * Выбор периода шагом (макет SalesReports: «Месяц · Квартал · Год» и стрелки).
 *
 * Календарные периоды, а не две произвольные даты: отчёты сравнивают квартал с кварталом,
 * и окно «с 3 по 17 число» даёт цифры, которые не с чем сравнить.
 */
export function PeriodBar({ value, onChange }: { value: StepPeriod; onChange: (next: StepPeriod) => void }) {
  const { t, locale } = useApp()
  const anchorDate = () => {
    const [year, month] = value.anchor.split('-').map(Number) as [number, number]
    return new Date(year, month - 1, 1)
  }
  return (
    <>
      <div className="seg" role="group" aria-label={t('rk.period.step')}>
        {(['month', 'quarter', 'year'] as const).map((step) => (
          <button
            key={step}
            type="button"
            aria-pressed={value.step === step}
            onClick={() => onChange(periodOf(step, anchorDate()))}
          >
            {t(`rk.period.${step}`)}
          </button>
        ))}
      </div>
      <div className="period-step">
        <button
          className="btn btn-ghost btn-sm btn-icon"
          type="button"
          aria-label={t('rk.period.prev')}
          onClick={() => onChange(shiftPeriod(value, -1))}
        >
          <Icon name="arrow-left" size="sm" />
        </button>
        <span className="input input-sm wa-period-label" aria-live="polite">
          <Icon name="meet" size="sm" />
          {periodLabel(value, t, locale)}
        </span>
        <button
          className="btn btn-ghost btn-sm btn-icon"
          type="button"
          aria-label={t('rk.period.next')}
          onClick={() => onChange(shiftPeriod(value, 1))}
        >
          <Icon name="arrow-right" size="sm" />
        </button>
      </div>
    </>
  )
}

// ------------------------------------------------------------------ названия по идентификаторам

/**
 * Названия записей по идентификаторам — через механизм списков.
 *
 * Отчёты аналитики отдают разрезы идентификаторами (сотрудник, клиент, товар). Название
 * берётся из списка той же сущности, а не своим эндпоинтом: список уже сужен областью
 * видимости, и запись, которую смотрящий не видит, останется без названия, а не
 * раскроется отчётом. Отказ по правам на справочник — не ошибка отчёта: строки остаются,
 * подписанные коротким идентификатором.
 */
export function useNames(
  entity: string | null,
  columns: string[],
  ids: (string | null | undefined)[],
): { names: Map<string, string>; failed: boolean } {
  const { rows, failed } = useRecords(entity, columns, ids)
  const names = new Map<string, string>()
  for (const [id, row] of rows) {
    const label = columns
      .map((column) => row[column])
      .filter((part) => part != null && part !== undefined && part !== '')
      .join(' ')
    if (label) names.set(id, label)
  }
  return { names, failed }
}

/**
 * Строки списка по идентификаторам: номер документа, клиент, сумма.
 *
 * Тот же путь, что у названий: список сужен областью видимости смотрящего, и чужая запись
 * просто не вернётся — экран покажет её без подробностей, а не раскроет.
 */
export function useRecords(
  entity: string | null,
  columns: string[],
  ids: (string | null | undefined)[],
): { rows: Map<string, ListRow>; failed: boolean; isLoading: boolean } {
  const unique = Array.from(new Set(ids.filter((id): id is string => Boolean(id)))).sort()
  const query = useQuery({
    queryKey: ['rk-records', entity, columns, unique],
    enabled: Boolean(entity) && unique.length > 0,
    retry: false,
    staleTime: 60 * 1000,
    queryFn: async ({ signal }) => {
      const rows = new Map<string, ListRow>()
      for (let at = 0; at < unique.length; at += 200) {
        const chunk = unique.slice(at, at + 200)
        const page = await fetchPage(
          entity!,
          {
            columns: ['id', ...columns],
            filters: [{ column: 'id', operator: 'in', value: chunk }],
            sort: [],
            pageSize: chunk.length,
          },
          null,
          signal,
        )
        for (const row of page.rows) rows.set(String(row.id), row)
      }
      return rows
    },
  })
  return { rows: query.data ?? new Map(), failed: Boolean(query.error), isLoading: query.isLoading }
}

/** Короткий идентификатор вместо названия — моноширинно, полный в подсказке. */
export function ShortId({ id }: { id: string | null | undefined }) {
  if (!id) return <span className="muted">—</span>
  return (
    <span className="mono t-sm secondary" title={id}>
      {id.slice(0, 8)}
    </span>
  )
}
